import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import notification from 'antd/es/notification';
import { EVA_AUTH_TOKEN_KEY, EvaStorage } from '@engelvoelkersamericas/common-ui';

const TIMEOUT = 60 * 1000;
axios.defaults.timeout = TIMEOUT;
axios.defaults.withCredentials = true;

export const NOT_ALLOWED_MESSAGE = 'You do not have permission to do that.';

/** The EVA services refuse a request with `{ error: string }`; returns that message, or undefined for any other body. */
const readServiceError = (data: unknown): string | undefined => {
  const error = data && typeof data === 'object' ? (data as { error?: unknown }).error : undefined;
  return typeof error === 'string' && error ? error : undefined;
};

const setupAxiosInterceptors = (onUnauthenticated: () => void) => {
  const onRequestSuccess = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Create a helper function to safely get and type-check storage values
    const getStorageValue = <T>(storage: { get(key: string): T }): string | null => {
      const value = storage.get(EVA_AUTH_TOKEN_KEY);
      return typeof value === 'string' ? value : null;
    };

    // Get token from local or session storage with proper type checking
    const token = getStorageValue(EvaStorage.local) || getStorageValue(EvaStorage.session);
    if (token) {
      // Set the Authorization header directly
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  };
  const onResponseSuccess = (response: AxiosResponse): AxiosResponse => response;
  const onResponseError = (err: AxiosError): Promise<never> => {
    const status = err.response?.status || 0;
    if (status === 401) {
      // Only a missing or expired token ends the session; the authorizer answers those with 401
      onUnauthenticated();
    } else if (status === 403) {
      // A permission denial keeps the session; the signed-in person just cannot reach this record or action
      notification.error({ message: 'Not allowed', description: readServiceError(err.response?.data) ?? NOT_ALLOWED_MESSAGE });
    } else if (status === 400 || (status > 403 && status < 562)) {
      // INFO: Show notification if any API call fail and status code within range of 404 to 562
      const errorDescription = err.response?.data || err.message;
      notification.error({
        message: 'Fail',
        description: readServiceError(err.response?.data) ?? (JSON.stringify(errorDescription) || 'Unknown error!'),
      });
    }
    return Promise.reject(err);
  };
  axios.interceptors.request.use(onRequestSuccess);
  axios.interceptors.response.use(onResponseSuccess, onResponseError);
};

export default setupAxiosInterceptors;
